import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { LifeResourceIdentifier, InterviewDisclosureIdentifier, ConditionDisclosureResource, DisclosurePhysicianResource, PrimaryPhysicianResource, PhysicianResource, ListDefinitionResource, ListItemResource, AisRestService, PhysicianResourceIdentifier, CaseResourceIdentifier, DisclosurePhysicianIdentifier } from '@Components/mras/core';

@Component({
    selector: 'jhi-physician-capture',
    templateUrl: './physician-capture.component.html',
    styles: []
})
export class PhysicianCaptureComponent implements OnInit {
    readonly _ = _; // create assignment so lodash can be used in template

    @Input()
        name: string;

    @Input()
        lifeIdentifier: LifeResourceIdentifier;

    @Input()
        primary: boolean;
    @Input()
        summaryMode: boolean;
    @Input()
        disclosureIdentifier: InterviewDisclosureIdentifier;

    @Input()
        disclosure: ConditionDisclosureResource;
    @Output()
        disclosureChange = new EventEmitter<ConditionDisclosureResource>();

    @Input()
        disclosurePhysicians: DisclosurePhysicianResource[];
    @Output()
        disclosurePhysiciansChange = new EventEmitter<DisclosurePhysicianResource[]>();

    @Input()
        primaryPhysician: PrimaryPhysicianResource;
    @Output()
        primaryPhysicianChange = new EventEmitter<PrimaryPhysicianResource>();

    @Input()
        physicians: PhysicianResource[];
    @Output()
        physiciansChange = new EventEmitter<PhysicianResource[]>();

    physiciansValue: PhysicianResource[];
    disclosurePhysiciansValue: DisclosurePhysicianResource[];

    @Input()
        countries: ListDefinitionResource;

    hasPhysician: boolean;
    hasPhysicianLabel: string;
    countryStatesMap = new Map<PhysicianResource, ListItemResource[]>();

    constructor(private aisRestService: AisRestService) {}

    ngOnInit(): void {
        this.physiciansValue = [];
        if (this.primary) {
            this.hasPhysicianLabel = 'Do you have a primary care physician or medical center?';
            if (this.primaryPhysician != null) {
                const primaryPhysician = (this.physicians || []).find(e => e.uuid === this.primaryPhysician[0].physicianIdentifier.uuid);
                if (primaryPhysician != null) {
                    this.hasPhysician = true;
                    this.physiciansValue.push(primaryPhysician);
                    this.fetchStatesForCountry(primaryPhysician);
                }
            }
        } else {
            this.hasPhysicianLabel = `Do you have a primary care physician, or do you go to a health care facility for ${this.disclosure.aliasName}?`;
            this.disclosurePhysiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => {
                    const resource = this.disclosurePhysicians.find(f => f.physicianIdentifier.uuid === e.physicianUuid);
                    const copy = new DisclosurePhysicianResource();
                    return Object.assign(copy, resource);
                })
                .filter(e => e != null);
            this.physiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => this.physicians.find(f => f.uuid === e.physicianUuid))
                .map(e => {
                    const copy = Object.assign(new PhysicianResource(), e);
                    this.fetchStatesForCountry(copy);
                    return copy;
                })
                .filter(e => e != null);
            this.hasPhysician = this.disclosure.hasPhysicians;
        }
    }

    submit($index: number) {
        let physicianResource;
        let caseId;
        if (this.primary) {
            physicianResource = this.physiciansValue[0];
            caseId = this.lifeIdentifier.caseId;
        } else {
            physicianResource = this.physiciansValue[$index];
            caseId = this.disclosureIdentifier.caseId;
        }

        let physician$: Observable<PhysicianResource>;
        const isUpdate = physicianResource.uuid != null;
        if (isUpdate) {
            const identifier = new PhysicianResourceIdentifier();
            identifier.caseId = caseId;
            identifier.uuid = physicianResource.uuid;
            physician$ = this.aisRestService.updatePhysician(identifier, physicianResource);
        } else {
            const identifier = new CaseResourceIdentifier();
            identifier.caseId = caseId;
            physician$ = this.aisRestService.createPhysician(identifier, physicianResource);
        }

        physician$
            .pipe(
                tap(e => {
                    if (this.primary) {
                        this.physiciansValue[0].uuid = e.uuid;
                    } else {
                        this.physiciansValue[$index].uuid = e.uuid;
                    }
                    if (this.physicians) {
                        this.physicians.push(e);
                        this.physiciansChange.emit(this.physicians);
                    }
                }),
                map(e => {
                    const identifier = new PhysicianResourceIdentifier();
                    identifier.uuid = e.uuid;
                    identifier.caseId = caseId;
                    return identifier;
                })
            )
            .subscribe(physicianIdentifier => {
                if (this.primary) {
                    const primaryPhysicianResource = new PrimaryPhysicianResource();
                    primaryPhysicianResource.physicianExpected = this.hasPhysician;
                    primaryPhysicianResource.physicianIdentifier = physicianIdentifier;
                    primaryPhysicianResource.lastVisitDateExplicitlyUnknown = true;
                    if (isUpdate) {
                        this.aisRestService.updatePrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    } else {
                        this.aisRestService.createPrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    }
                } else {
                    const resource = this.disclosurePhysiciansValue[$index];
                    resource.lastVisitDateExplicitlyUnknown = false;
                    resource.physicianIdentifier = physicianIdentifier;
                    if (isUpdate) {
                        const disclosurePhysicianIdentifier = Object.assign(new DisclosurePhysicianIdentifier(), this.disclosureIdentifier);
                        disclosurePhysicianIdentifier.physicianUuid = resource.physicianIdentifier.uuid;
                        this.aisRestService.updateDisclosurePhysician(disclosurePhysicianIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);
                            });
                        });
                    } else {
                        this.aisRestService.createDisclosurePhysician(this.disclosureIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);
                            });
                        });
                    }
                }
            });
    }

    clear($index: number) {
        if (this.physiciansValue) {
            const uuid = this.physiciansValue[$index].uuid;
            Object.keys(this.physiciansValue[$index]).forEach(k => (this.physiciansValue[$index][k] = null));
            this.physiciansValue[$index].country = new ListItemResource();
            this.physiciansValue[$index].state = new ListItemResource();
            this.physiciansValue[$index].uuid = uuid;
        }
        if (this.disclosurePhysiciansValue) {
            Object.keys(this.disclosurePhysiciansValue[$index]).forEach(k => (this.disclosurePhysiciansValue[$index][k] = null));
        }
    }

    hasPhysicianAnswer(val: boolean) {
        this.hasPhysician = val;
        
        if (this.primary && !this.hasPhysician && this.primaryPhysician != null) {
            this.aisRestService.deletePrimaryPhysician(this.lifeIdentifier).subscribe(e => {
                this.primaryPhysician = null;
                this.physiciansValue[0].uuid = null;
                this.primaryPhysicianChange.emit(null);
            });
            return;
        }

        if (!this.primary) {
            const resource = new ConditionDisclosureResource();
            resource.hasPhysicians = this.hasPhysician;
            this.aisRestService.updateDisclosure(this.disclosureIdentifier, resource).subscribe(e => {
                this.hasPhysician = e.hasPhysicians;
            });
        }
    }

    getPhysicians() {
        if (this.physiciansValue.length === 0) {
            const physicianResource = new PhysicianResource();
            physicianResource.country = new ListItemResource();
            this.physiciansValue.push(physicianResource);
        }
        if (!this.primary && this.disclosurePhysiciansValue.length === 0) {
            this.disclosurePhysiciansValue.push(new DisclosurePhysicianResource());
        }
        return this.physiciansValue;
    }

    mapToOptionValueFromListItems(resource: ListItemResource): string {
        return resource.code;
    }

    mapToOptionLabelFromListItems(resource: ListItemResource): string {
        return resource.description;
    }

    fetchStatesForCountry(physician: PhysicianResource) {
        this.aisRestService.getStatesForCountry(this.lifeIdentifier, physician.country.code).subscribe(e => {
            physician.country.description = null;
            this.countryStatesMap.set(physician, e);
            if (e.length === 0) {
                physician.state = null;
            }
            if (e.length > 0 && physician.state == null) {
                physician.state = new ListItemResource();
                physician.stateString = null;
            }
        });
    }
}
